import { Icon } from "components";
import Button from "components/atoms/Button/Button";
import Heading from "components/atoms/layout/Heading/Heading";
import Popup from "components/molecules/Popup/Popup";
import React, { useEffect, useId, useState } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  createApiKey,
  deleteApiKey,
  getApiKeys,
} from "store/slices/api.slice";
import { toastHelper } from "utils/helper";
import { errorMessages, infoMessages, successMessages } from "utils/message";

export const ApiKey: React.FC<{ showSmall?: boolean }> = ({ showSmall }) => {
  const dispatch = useAppDispatch();
  const { data, isLoading, isCreating, isDeleting } = useAppSelector(
    (state) => state.apiKey
  );
  const [showKey, setShowKey] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const toastId = useId();

  const onCreateApiKey = async () => {
    toastHelper({ message: infoMessages.SAVING_DATA, type: "info", toastId });
    await dispatch(createApiKey())
      .unwrap()
      .then((response) => {
        if (response.isError) throw new Error(response.message);
        toastHelper({
          message: successMessages.ADD_SUCCESS,
          type: "success",
          toastId,
        });
        // Refresh the API keys list
        dispatch(getApiKeys());
      })
      .catch(() => {
        toastHelper({
          message: errorMessages.SOMETHING_WENT_WRONG,
          type: "error",
          toastId,
        });
      });
  };

  const onDeleteApiKey = async () => {
    toastHelper({ message: infoMessages.DELETING_DATA, type: "info", toastId });
    await dispatch(deleteApiKey())
      .unwrap()
      .then((response) => {
        if (response.isError) throw new Error(response.message);
        toastHelper({
          message: successMessages.DELETE_SUCCESS,
          type: "success",
          toastId,
        });
        setShowKey(false);
        setShowConfirmModal(false);
        // Refresh the API keys list
        dispatch(getApiKeys());
      })
      .catch(() => {
        toastHelper({
          message: errorMessages.SOMETHING_WENT_WRONG,
          type: "error",
          toastId,
        });
      });
  };

  const handleShowDeleteModal = () => {
    setShowConfirmModal(true);
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toastHelper({
      message: "API Key copied to clipboard!",
      type: "success",
      toastId,
    });
  };

  const maskApiKey = (key: string) => {
    if (!key) return "";
    if (showKey) return key;
    return key.slice(0, 3) + "..." + key.slice(-2);
  };

  useEffect(() => {
    dispatch(getApiKeys());
  }, [dispatch]);

  const hasApiKey = data && data.length > 0 && data[0]?.apiKey;

  return (
    <>
      <div className="flex flex-col gap-4">
        {showSmall ? (
          <div className="flex flex-row  gap-4">
            <Icon name="code" size={24} className="text-primary-500" />
            <div className="flex flex-col gap-1">
              <h1 className="font-bold capitalize ">Service API Key</h1>
            </div>
          </div>
        ) : (
          <Heading icon="url" iconSize={24} title="Service API Key" />
        )}

        {isLoading ? (
          <div className="flex flex-col gap-4">
            <p className="text-gray-600">Loading API keys...</p>
          </div>
        ) : hasApiKey ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 p-3 bg-gray-800 rounded-lg border border-gray-600">
              <code
                className="flex-1 text-sm font-mono text-white-500"
                style={{ color: "var(--color-white-500)" }}
              >
                {maskApiKey(data[0]?.apiKey || "")}
              </code>
              <button
                onClick={() => setShowKey(!showKey)}
                className="p-1 text-gray-400 hover:text-white-500"
                title={showKey ? "Hide API Key" : "Show API Key"}
              >
                <Icon name={showKey ? "eye-off" : "eye"} size={16} />
              </button>
              <button
                onClick={() => copyToClipboard(data[0]?.apiKey || "")}
                className="p-1 text-gray-400 hover:text-white-500"
                title="Copy to clipboard"
              >
                <Icon name="copy" size={16} />
              </button>
            </div>
            <div className="flex gap-4">
              <Button
                action="button"
                size="sm"
                iconName="trash"
                isProcessing={isDeleting}
                iconSize={16}
                onClick={handleShowDeleteModal}
              >
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-white-500">
              No API key generated yet. Create one to start integrating with
              your applications.
            </p>
            <div className="flex gap-4">
              <Button
                action="button"
                iconName="plus"
                size="sm"
                isProcessing={isCreating}
                onClick={onCreateApiKey}
              >
                Generate API Key
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de confirmação para exclusão */}
      {showConfirmModal && (
        <Popup onClose={handleCancelDelete} width="sm">
          <div className="flex flex-col gap-6 p-6">
            <div className="flex flex-col gap-4">
              <h2 className="text-xl font-bold text-white-500">
                Confirm Deletion
              </h2>
              <p className="text-gray-400">
                Are you sure you want to delete this API key? This action cannot
                be undone and will immediately revoke access for any
                applications using this key.
              </p>
            </div>

            <div className="flex gap-4">
              <Button
                action="button"
                size="sm"
                iconName="check"
                isProcessing={isDeleting}
                iconSize={16}
                onClick={onDeleteApiKey}
                type="primary"
              >
                Yes, Delete
              </Button>
              <Button
                action="button"
                size="sm"
                iconName="close"
                iconSize={16}
                onClick={handleCancelDelete}
                type="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Popup>
      )}
    </>
  );
};
